import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useTimer } from '../../context/TimerContext';
import { useDashboard } from '../../context/DashboardContext';
import QuickAddModal from './QuickAddModal';
import { useTranslation } from 'react-i18next';

interface DailyWidgetProps {
  onOpenQuickAdd?: () => void;
}

const DailyWidget: React.FC<DailyWidgetProps> = ({ onOpenQuickAdd }) => {
  const { isDark } = useTheme();
  const { activeTimer, elapsedSeconds } = useTimer();
  const { todayEntries, totalHoursThisWeek } = useDashboard();
  const { t } = useTranslation();
  const [showQuickAdd, setShowQuickAdd] = useState(false);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const todayTotal = todayEntries.reduce((sum, e) => sum + e.hours_worked, 0);
  const todayGoal = 8;
  const todayRemaining = Math.max(0, todayGoal - todayTotal);

  const widgetBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const textColor = isDark ? 'text-white' : 'text-slate-900';
  const mutedText = isDark ? 'text-slate-400' : 'text-slate-600';

  return (
    <>
      <div className={`rounded-lg border p-6 ${widgetBg}`}>
        <h2 className={`text-lg font-semibold mb-4 ${textColor}`}>{t('dashboard.today')}</h2>

        {/* Active Timer Section */}
        {activeTimer ? (
          <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-lg">
            <div className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'} mb-2`}>
              {t('dashboard.activeTimer')}
            </div>
            <div className={`text-2xl font-bold ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>
              {formatTime(elapsedSeconds)}
            </div>
            <div className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-800'} mt-1`}>
              {activeTimer.project?.name}
            </div>
          </div>
        ) : null}

        {/* Daily Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-baseline mb-2">
            <span className={`text-sm font-medium ${mutedText}`}>{t('dashboard.dailyProgress')}</span>
            <span className={`text-sm ${mutedText}`}>
              {todayTotal.toFixed(1)}h / {todayGoal}h
            </span>
          </div>
          <div className={`w-full h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-slate-200'} overflow-hidden`}>
            <div
              className="h-full bg-green-500 transition-all duration-300"
              style={{ width: `${Math.min(100, (todayTotal / todayGoal) * 100)}%` }}
            ></div>
          </div>
          {todayRemaining > 0 && (
            <div className={`text-xs mt-2 ${mutedText}`}>
              {todayRemaining.toFixed(1)}h remaining
            </div>
          )}
        </div>

        {/* Today's Entries */}
        {todayEntries.length > 0 ? (
          <div className="mb-6">
            <h3 className={`text-sm font-semibold mb-3 ${textColor}`}>{t('dashboard.entries')}</h3>
            <div className="space-y-2">
              {todayEntries.map(entry => (
                <div key={entry.id} className={`flex justify-between text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <span>{entry.project_name}</span>
                  <span className="font-medium">{entry.hours_worked}h</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Quick Add Button */}
        <button
          onClick={() => {
            setShowQuickAdd(true);
            onOpenQuickAdd?.();
          }}
          className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
        >
          {t('dashboard.addTime')}
        </button>
      </div>

      {/* Quick Add Modal */}
      <QuickAddModal
        isOpen={showQuickAdd}
        onClose={() => setShowQuickAdd(false)}
        todayTotal={todayTotal}
      />
    </>
  );
};

export default DailyWidget;
